import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ViewColumnsIcon } from '@heroicons/react/24/outline';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import myRequestsEmptyAnimation from '@/assets/lottie/myRequestsEmptyAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import { Pagination, Column, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import {
  ColumnCustomizationPopup,
  type ColumnOption,
} from '@shared/components/ColumnCustomizationPopup';
import { Tooltip } from '@shared/components/Tooltip';
import { Menu, MenuVariant, MenuSize } from '@shared/components/Menu';
import { MY_REQUESTS_LABELS as L } from '../../constants/labels';
import { SERVICE_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';
import type {
  MyAssignedTaskListItem,
  ServiceRequestStatus,
} from '../../types/serviceRequestListType';
import { useMyAssignedTasksList } from './useMyAssignedTasksList';
import { STATUS_CONFIG } from '../viewServiceRequest/viewServiceRequestHelpers';
import { formatScheduledDate } from '../../utils/scheduledWindow';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MY_TASKS_SEARCH_PLACEHOLDERS = [
  TL.MY_TASKS_SEARCH_PLACEHOLDER_REQUEST_ID.polish,
  TL.MY_TASKS_SEARCH_PLACEHOLDER_CLIENT_NAME.polish,
  TL.MY_TASKS_SEARCH_PLACEHOLDER_EMPLOYEE_NAME.polish,
];

function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ─── Avatar / cell components ─────────────────────────────────────────────────

function PersonAvatar({ name }: { name: string }) {
  return (
    <div className="w-8 h-8 rounded-full bg-[#E8EEF0] flex items-center justify-center shrink-0 overflow-hidden">
      <span className="text-[#223746] text-sm font-medium leading-none">
        {getInitials(name)}
      </span>
    </div>
  );
}

function FacilityAvatar({ name }: { name: string }) {
  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
      style={{ backgroundColor: '#E5E7EB' }}
    >
      <span className="text-[#223746] text-sm font-medium leading-none">
        {getInitials(name)}
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: ServiceRequestStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1 rounded-[8px] whitespace-nowrap"
      style={{
        backgroundColor: cfg.bg,
        color: cfg.text,
        fontSize: 14,
        fontWeight: 500,
        lineHeight: '20px',
        letterSpacing: '0.1px',
      }}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: cfg.text }}
      />
      {cfg.label}
    </span>
  );
}

function PersonCell({ name }: { name: string | null }) {
  if (!name) {
    return (
      <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
        {L.EMPTY_CELL}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-0">
      <PersonAvatar name={name} />
      <div className="min-w-0 flex-1 overflow-hidden">
        <Tooltip content={name}>
          <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {name}
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

function FacilityCell({ name }: { name: string | null }) {
  if (!name) {
    return (
      <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
        {L.EMPTY_CELL}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-0">
      <FacilityAvatar name={name} />
      <div className="min-w-0 flex-1 overflow-hidden">
        <Tooltip content={name}>
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {name}
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

function DatePill({ value }: { value: string }) {
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-md bg-[#F2F4F5] text-[#223746] text-sm font-medium leading-5 tracking-[0.25px] whitespace-nowrap">
      {value}
    </span>
  );
}

function ScheduledDateCell({
  start,
  end,
  timeZone,
}: {
  start: string | null;
  end: string | null;
  timeZone: string | null;
}) {
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <DatePill value={formatScheduledDate(start, timeZone)} />
      <span className="text-[#474A4C] text-sm leading-5">-</span>
      <DatePill value={formatScheduledDate(end, timeZone)} />
    </div>
  );
}

// ─── Column options ───────────────────────────────────────────────────────────

const COLUMN_OPTIONS: ColumnOption[] = [
  { key: 'service_request_id', label: L.COLUMN_REQUEST_ID, disabled: true },
  { key: 'equipment_name', label: L.COLUMN_EQUIPMENT_NAME, disabled: true },
  { key: 'service_type', label: L.COLUMN_SERVICE_TYPE, disabled: true },
  { key: 'scheduled_date', label: L.COLUMN_SCHEDULED_DATE },
  { key: 'client_name', label: L.COLUMN_CLIENT_NAME },
  { key: 'client_id', label: L.COLUMN_CLIENT_ID },
  { key: 'facility_name', label: L.COLUMN_FACILITY_NAME },
  { key: 'facility_id', label: L.COLUMN_FACILITY_ID },
  { key: 'status', label: L.COLUMN_STATUS, disabled: true },
];

const DEFAULT_VISIBLE_COLUMNS: Record<string, boolean> = Object.fromEntries(
  COLUMN_OPTIONS.map((o) => [o.key, true])
);

const COLUMNS_URL_PARAM = 'cols';
const DEFAULT_COLS_STRING = COLUMN_OPTIONS.map((o) => o.key).join(',');

function parseColsParam(value: string | null): Record<string, boolean> | null {
  if (!value) {
    return null;
  }
  const validKeys = new Set(COLUMN_OPTIONS.map((o) => o.key));
  const visible = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => validKeys.has(s));
  if (visible.length === 0) {
    return null;
  }
  const result: Record<string, boolean> = {};
  COLUMN_OPTIONS.forEach((o) => {
    result[o.key] = visible.includes(o.key);
  });
  return result;
}

function buildColsParam(visibleKeys: Record<string, boolean>): string {
  return COLUMN_OPTIONS.filter((o) => visibleKeys[o.key] !== false)
    .map((o) => o.key)
    .join(',');
}

// ─── Component ────────────────────────────────────────────────────────────────

export const MyAssignedTasksTab: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    search,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    hasNoData,
    isSearchClearing,
  } = useMyAssignedTasksList();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnPopupOpen, setIsColumnPopupOpen] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<
    Record<string, boolean>
  >(
    () =>
      parseColsParam(searchParams.get(COLUMNS_URL_PARAM)) ?? {
        ...DEFAULT_VISIBLE_COLUMNS,
      }
  );

  const colsParam = searchParams.get(COLUMNS_URL_PARAM);
  useEffect(() => {
    setVisibleColumnKeys(
      parseColsParam(colsParam) ?? { ...DEFAULT_VISIBLE_COLUMNS }
    );
  }, [colsParam]);

  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const columnContainerRef = useRef<HTMLDivElement | null>(null);

  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const menuPortalRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Rotating search placeholder: cycle through the searchable fields one at a
  // time while the field is empty, so users can discover what they can search
  // by. Rotation pauses once a query is entered (the placeholder is hidden).
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    if (search) {
      return;
    }
    const intervalId = setInterval(() => {
      setPlaceholderIndex(
        (prev) => (prev + 1) % MY_TASKS_SEARCH_PLACEHOLDERS.length
      );
    }, 3000);
    return () => clearInterval(intervalId);
  }, [search]);

  const handleCloseFilter = useCallback(() => {
    setDateRange(appliedFilters.dateRange);
    setIsFilterOpen(false);
  }, [appliedFilters, setDateRange]);

  const handleCloseColumnPopup = useCallback(() => {
    setIsColumnPopupOpen(false);
  }, []);

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleColumnVisibilityChange = (key: string, visible: boolean) => {
    const next = { ...visibleColumnKeys, [key]: visible };
    setVisibleColumnKeys(next);
    const colsString = buildColsParam(next);
    setSearchParams(
      (p) => {
        const updated = new URLSearchParams(p);
        if (colsString === DEFAULT_COLS_STRING) {
          updated.delete(COLUMNS_URL_PARAM);
        } else {
          updated.set(COLUMNS_URL_PARAM, colsString);
        }
        return updated;
      },
      { replace: true }
    );
  };

  // Outside-click for filter and column popups
  useEffect(() => {
    if (!isFilterOpen && !isColumnPopupOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        isFilterOpen &&
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
      if (
        isColumnPopupOpen &&
        columnContainerRef.current &&
        !columnContainerRef.current.contains(target)
      ) {
        handleCloseColumnPopup();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    isFilterOpen,
    isColumnPopupOpen,
    handleCloseFilter,
    handleCloseColumnPopup,
  ]);

  // Outside-click for action menu
  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = menuTriggerRef.current?.contains(target) ?? false;
      const insidePortal = menuPortalRef.current?.contains(target) ?? false;
      if (!insideTrigger && !insidePortal) {
        setOpenMenuRowId(null);
        setMenuPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]);

  const columns: Column<MyAssignedTaskListItem>[] = useMemo(
    () => [
      {
        key: 'service_request_id',
        header: L.COLUMN_REQUEST_ID,
        gridTrack: 'minmax(160px, 1fr)',
        render: (row) => (
          <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] whitespace-nowrap">
            {row.service_request_id}
          </span>
        ),
      },
      {
        key: 'equipment_name',
        header: L.COLUMN_EQUIPMENT_NAME,
        gridTrack: 'minmax(220px, 1fr)',
        render: (row) => (
          <Tooltip content={row.equipment_name ?? L.EMPTY_CELL}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.equipment_name ?? L.EMPTY_CELL}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'service_type',
        header: L.COLUMN_SERVICE_TYPE,
        gridTrack: 'minmax(200px, 1fr)',
        render: (row) => (
          <Tooltip content={row.service_type ?? L.EMPTY_CELL}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.service_type ?? L.EMPTY_CELL}
            </span>
          </Tooltip>
        ),
      },
      {
        key: 'scheduled_date',
        header: L.COLUMN_SCHEDULED_DATE,
        gridTrack: 'minmax(260px, 1fr)',
        render: (row) => (
          <ScheduledDateCell
            start={row.scheduled_start_at}
            end={row.scheduled_end_at}
            timeZone={row.scheduled_timezone}
          />
        ),
      },
      {
        key: 'client_name',
        header: L.COLUMN_CLIENT_NAME,
        gridTrack: 'minmax(220px, 1fr)',
        render: (row) => <PersonCell name={row.client_name} />,
      },
      {
        key: 'client_id',
        header: L.COLUMN_CLIENT_ID,
        gridTrack: 'minmax(180px, 1fr)',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap">
            {row.client_id ?? L.EMPTY_CELL}
          </span>
        ),
      },
      {
        key: 'facility_name',
        header: L.COLUMN_FACILITY_NAME,
        gridTrack: 'minmax(220px, 1fr)',
        render: (row) => <FacilityCell name={row.facility_name} />,
      },
      {
        key: 'facility_id',
        header: L.COLUMN_FACILITY_ID,
        gridTrack: 'minmax(200px, 1fr)',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap">
            {row.facility_id ?? L.EMPTY_CELL}
          </span>
        ),
      },
      {
        key: 'status',
        header: L.COLUMN_STATUS,
        gridTrack: 'minmax(220px, 1fr)',
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
        key: 'actions',
        header: L.COLUMN_ACTIONS,
        width: '80px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div className="inline-flex items-center justify-center w-full">
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const rect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const menuHeight = 100;
                    setMenuPosition({
                      top:
                        spaceBelow < menuHeight + 8
                          ? rect.top - menuHeight - 4
                          : rect.bottom + 4,
                      right: window.innerWidth - rect.right,
                    });
                  } else {
                    setMenuPosition(null);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={`Akcje dla zadania ${row.service_request_id}`}
                data-testid={`my-tasks-action-button-${index ?? 0}`}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          );
        },
      },
    ],
    [openMenuRowId]
  );

  const visibleColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.key === 'actions' || visibleColumnKeys[col.key] !== false
      ),
    [columns, visibleColumnKeys]
  );

  const filterCount =
    (appliedFilters.dateRange.startDate ? 1 : 0) +
    (appliedFilters.dateRange.endDate ? 1 : 0);

  const noResults =
    !loading && !isSearchClearing && items.length === 0 && !hasNoData;

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      {/* Toolbar */}
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <div className="w-full max-w-[325px] mr-4">
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={
                MY_TASKS_SEARCH_PLACEHOLDERS[placeholderIndex] ??
                TL.MY_TASKS_SEARCH_PLACEHOLDER.polish
              }
              inputTestId="my-tasks-search-input"
              fluid
            />
          </div>
          <div className="flex items-center gap-2">
            <div ref={filterContainerRef} className="relative">
              <FilterBar
                onClick={() => {
                  setIsColumnPopupOpen(false);
                  setIsFilterOpen((prev) => !prev);
                }}
                isOpen={isFilterOpen}
                filterCount={filterCount}
                label={L.FILTER_BUTTON}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={handleCloseFilter}
                showDateRange={true}
                allowFutureDates
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                onApply={handleApplyFiltersAndClose}
                onClear={handleClearFilters}
              />
            </div>

            <div ref={columnContainerRef} className="relative">
              <button
                type="button"
                onClick={() => {
                  setIsFilterOpen(false);
                  setIsColumnPopupOpen((prev) => !prev);
                }}
                className="flex items-center justify-center rounded-lg border border-[#DCDDE0] px-[8px] py-[8px] cursor-pointer transition-all duration-200 ease-in-out hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-label={L.COLUMNS_BUTTON}
                aria-expanded={isColumnPopupOpen}
                aria-haspopup="dialog"
                data-testid="my-tasks-column-button"
              >
                <ViewColumnsIcon
                  className="h-6 w-6 text-on-surface-variant shrink-0"
                  aria-hidden
                />
              </button>
              <ColumnCustomizationPopup
                isOpen={isColumnPopupOpen}
                onClose={handleCloseColumnPopup}
                columnOptions={COLUMN_OPTIONS}
                visibleKeys={visibleColumnKeys}
                onVisibilityChange={handleColumnVisibilityChange}
                disabledTooltipText="Ta kolumna jest wymagana"
              />
            </div>
          </div>
        </div>
      )}

      {/* Table area */}
      <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
        {hasNoData ? (
          <div
            data-testid="my-tasks-empty-state"
            className="flex flex-col items-center justify-center w-full h-full py-12"
          >
            {/* Shift left to match the same animation on the "Moje zgłoszenia" screen */}
            <div style={{ transform: 'translateX(-50px)' }}>
              <LottieAnimation
                animationData={myRequestsEmptyAnimation}
                loop
                width={300}
                aspectRatio="1 / 1"
              />
            </div>
            <p className="text-bodyLarge font-medium text-center text-[#474A4C] mt-4">
              {L.EMPTY_MY_TASKS_TITLE}
            </p>
            <p className="text-bodyLarge text-center text-[#474A4C] mt-2 max-w-sm">
              {L.EMPTY_MY_TASKS_DESCRIPTION}
            </p>
          </div>
        ) : noResults ? (
          <div
            data-testid="my-tasks-no-results"
            className="flex flex-col items-center justify-center w-full h-full gap-3"
          >
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="w-[548px] max-w-full flex flex-col items-center justify-start gap-1">
              <div className="w-full text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                {L.NO_RESULTS_TITLE}
              </div>
              <div className="w-full text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                {(() => {
                  const hasSearch = search.trim() !== '';
                  const hasFilter =
                    !!appliedFilters.dateRange.startDate ||
                    !!appliedFilters.dateRange.endDate;
                  if (hasSearch && hasFilter) {
                    return L.NO_RESULTS_DESCRIPTION_BOTH;
                  }
                  if (hasSearch) {
                    return L.NO_RESULTS_DESCRIPTION_SEARCH;
                  }
                  if (hasFilter) {
                    return L.NO_RESULTS_DESCRIPTION_FILTER;
                  }
                  return L.NO_RESULTS_DESCRIPTION_BOTH;
                })()}
              </div>
            </div>
          </div>
        ) : (
          <Table
            columns={visibleColumns}
            data={items}
            loading={loading}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
          />
        )}
      </div>

      {/* Pagination */}
      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </div>
          )}
        </div>
      )}

      {/* Actions menu portal */}
      {openMenuRowId !== null &&
        menuPosition !== null &&
        createPortal(
          <div
            ref={menuPortalRef}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              right: menuPosition.right,
              zIndex: 9999,
            }}
          >
            <Menu
              items={[{ id: 'VIEW', label: L.ACTION_VIEW }]}
              variant={MenuVariant.Light}
              size={MenuSize.Small}
              toolTip={false}
              onItemClick={(itemId) => {
                const rowId = openMenuRowId;
                setOpenMenuRowId(null);
                setMenuPosition(null);
                if (itemId === 'VIEW' && rowId) {
                  navigate(`/Zgłoszenia/Podgląd-mojego-zadania/${rowId}`);
                }
              }}
              showLeadingIcons={false}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default MyAssignedTasksTab;
